import type { PartnershipsContent } from './index'

// English copy, taken verbatim from the partnerships copy file (9 Sep 2026).
//
// Held back on purpose, pending the open items in the build brief: every FAQ
// block, the data-ownership sentence, the access mechanism, the pricing shape,
// and the claim that an employer never sees family data. Those are marked
// [CONFIRM] in the copy file, which says not to publish them as written, so
// they are absent rather than guessed — including from the workplaces meta
// description, where the brief flagged the assertion.
const en: PartnershipsContent = {
  breadcrumbHub: 'For organisations',

  hub: {
    meta: {
      title: 'Homie for organisations | Family organizer partnerships',
      description:
        'One calm place for the family week, offered through your organisation. Free for the first year for employers, councils and unions, then two ways to continue.',
    },
    h1: ['Every person you support', 'is running a second shift at home'],
    subhead: ['A simple household organizer,', 'offered through your organisation.'],
    heroCta: 'Become a partner',

    opening: {
      eyebrow: 'Nobody puts it on',
      highlight: 'A TIMESHEET',
      paragraphs: [
        'The school emails, the dentist, whose turn it is for football kit, the thing that has to be bought before Thursday. It runs in the background all day, in somebody’s head, and it does not stop when they sit down at work.',
        'Researchers call it the mental load. Most organisations never see it, because from the outside it does not look like anything.',
        'We built Homie because we were living it. One shared place for the family week, so the whole household can see what is happening, not just the parent holding it together.',
      ],
    },

    fork: {
      eyebrow: 'Two ways to bring Homie to the people',
      highlight: 'YOU LOOK AFTER',
      workplaces: {
        label: 'Workplaces',
        line: 'For the people you employ.',
        examples: 'Employers, HR teams, unions.',
      },
      communities: {
        label: 'Communities',
        line: 'For the people you serve.',
        examples: 'Councils, family centres, schools, charities.',
      },
    },

    whatFamiliesGet: {
      eyebrow: 'What families',
      highlight: 'ACTUALLY GET',
      paragraphs: [
        'One shared calendar the whole family can see and edit. Tasks that do not need a parent to chase them. Routines for mornings, school and bedtime. Meal planning, with recipes that turn into a shopping list.',
        'It works on iOS and Android, offline as well as on, in Norwegian and every other major language. There is a display mode for a screen on the kitchen wall, included in every subscription.',
        'No ads. No AI. Nothing about a family sold to anyone.',
      ],
    },

    startSmall: {
      eyebrow: 'A low-risk way to',
      highlight: 'START SMALL',
      paragraphs: [
        'Partner organisations get the first year free. No licence fee, no setup cost, no commitment at the end of it.',
        'We do it this way because a year is long enough to find out whether families actually use it. Three months is not. Based on your goals, we can see what is and isn’t working for your team, so you can decide how to continue the partnership.',
      ],
    },

    models: {
      eyebrow: 'After the trial year,',
      highlight: 'TWO WAYS TO PARTNER',
      funded: {
        title: 'Funded access',
        body: 'Your organisation pays an annual licence and families keep using Homie at no cost to them.',
      },
      official: {
        title: 'Official partner',
        body: 'No cost to your organisation. Families who want to carry on move to a normal Homie subscription, and you stay listed as the partner who brought it to them.',
      },
      closing:
        'Most employers land on the first. Most community organisations without a budget line land on the second. Neither is the default, and you can change your mind at the end of the year.',
    },

    story: {
      eyebrow: 'The story',
      highlight: 'BEHIND IT',
      paragraphs: [
        'Homie was built by a Norwegian family, for a Norwegian family, before it was built for anyone else.',
        'A father who is a self-described structure geek, probably a coping mechanism for his ADHD. A wife who is the toughest critic in the house and took a month to try it. An eleven-year-old who could not remember her gym bag, and a mother acting as a human reminder, which was a sub par solution for both of them.',
        'We wanted something that feels like a whiteboard on the fridge, only better. That is still the whole idea.',
      ],
    },

    cta: {
      heading: 'Become a partner',
      blurb: 'Tell us who you look after and we will send a one-page overview. No pitch deck.',
      action: 'Contact us',
    },
  },

  workplaces: {
    meta: {
      title: 'A family organizer as an employee benefit | Homie',
      description:
        'Your people are running a second job at home. Give them one shared family organizer, free for the first year.',
    },
    h1: ['Your people are running', 'a second job at home.'],
    subhead: ['One calm place for the family week,', 'offered through you.'],
    heroCta: 'Become a partner',
    breadcrumbName: 'Workplaces',

    sections: [
      {
        eyebrow: 'It almost never shows up in',
        highlight: 'AN ENGAGEMENT SURVEY',
        paragraphs: [
          'It looks like someone being a bit slower to answer. Leaving at five on the dot. Booking the same Thursday off every few weeks and not saying why. Handing in notice in March with no clear reason.',
          'Underneath most of that is a household running on one person’s memory. The forms, the appointments, the kit, the pickup. It is real work and it is invisible, which is why it almost never shows up in an engagement survey.',
          'You cannot take that load off someone, but making it something visible that’s shared with the family goes a long way. That’s where Homie comes in.',
        ],
      },
      {
        eyebrow: 'Who this',
        highlight: 'IS FOR',
        paragraphs: [
          'Employers of any size. HR and people teams. Unions buying on behalf of members as employees.',
          'It suits organisations where a good share of staff are parents or carers, and where the benefits budget is small enough that childcare subsidies and fertility cover are out of reach.',
        ],
      },
      {
        eyebrow: 'Why this is not',
        highlight: 'ANOTHER WELLBEING APP',
        paragraphs: [
          'Most family benefits are built around babies. Childcare subsidies, nursery partnerships, fertility cover. All of it drops away once a child starts school, and the coordination load goes up, not down.',
          'Homie is used by the whole household, including the children. An eleven-year-old can check what she needs for tomorrow without asking anyone. That is the part that gives a parent their evening back.',
          'And you will know whether it landed. Families either use a shared calendar or they do not, and you will see which happened before you pay anything.',
        ],
      },
      {
        eyebrow: 'How it',
        highlight: 'WORKS',
        paragraphs: [
          'You offer Homie to staff.',
          'Families set it up themselves, at home, in their own time. Nothing lands on HR beyond telling people it exists.',
        ],
      },
      {
        eyebrow: 'What it',
        highlight: 'COSTS',
        paragraphs: [
          'The first year is free. No licence fee, no setup cost, no commitment at the end.',
          'After that, most employers move to funded access, where the organisation pays an annual licence and families pay nothing.',
        ],
        hubLink: {
          before: 'The trial year, the two partnership models and the story behind Homie are all on the ',
          label: 'partnerships overview',
          after: '.',
        },
      },
    ],

    cta: {
      heading: 'Become a partner',
      blurb: 'Tell us roughly how many families you employ and we will send a one-page overview.',
      action: 'Contact us',
    },

    crossLink: 'Looking for the version for the people you serve?',
  },

  communities: {
    meta: {
      title: 'Homie for councils and community organisations',
      description:
        'Offer the families you serve one calm place for the week. Free for the first year, no cost to families, nothing sold on. Start with one area.',
    },
    h1: ['The families you serve', 'are holding it all in their heads.'],
    subhead: ['One calm place for the week,', 'offered through your organisation.'],
    heroCta: 'Start a conversation',
    breadcrumbName: 'Communities',

    sections: [
      {
        eyebrow: 'One person holding',
        highlight: 'TOO MUCH AT ONCE',
        paragraphs: [
          'Every family runs on information that lives in somebody’s head. Where the form is, when the appointment is, who is collecting whom.',
          'For most families that is tiring. For a family already stretched, it is the thing that makes everything else harder. Behind a missed appointment there is almost always one person holding too much at once, rather than a family that stopped caring.',
          'A shared place for the week will not fix that on its own. It does take one specific weight off, and it is a weight that falls hardest on the households you are already working with.',
        ],
      },
      {
        eyebrow: 'Who this',
        highlight: 'IS FOR',
        paragraphs: [
          'Kommuner and councils. Family centres and family support services. Schools. Charities and voluntary organisations working with families. Unions and associations serving members as families rather than as employees.',
        ],
      },
      {
        eyebrow: 'What you',
        highlight: 'CAN OFFER',
        paragraphs: [
          'Homie, free to the families in your area, for a year.',
          'One shared calendar the household can see and edit. Tasks and routines that do not need an adult standing over them. Meal planning that turns into a shopping list.',
          'It works offline as well as online, which matters more than it sounds when data is something a household budgets for. It is in Norwegian and every other major language, so it does not only serve the families who already find services easy to reach.',
          'No ads. No AI. Nothing about a family sold to anyone.',
        ],
      },
      {
        eyebrow: 'Start',
        highlight: 'SMALL',
        paragraphs: [
          'You do not need to roll this out across a whole area to find out whether it works.',
          'Pick one district, or one service, or one group of families you already know well. Run it for the year. Pick one outcome you actually care about and measure that one properly, instead of building a dashboard nobody reads.',
          'If it has not helped by the end of the year, it has cost you nothing and we will say so ourselves.',
        ],
      },
      {
        eyebrow: 'What it',
        highlight: 'COSTS',
        paragraphs: [
          'The first year is free. No licence fee, no setup cost, no commitment.',
          'After that there are two ways to carry on. <b>Funded access</b>, where your organisation pays an annual licence and families continue at no cost to them. Or <b>official partner</b>, where there is no cost to your organisation and families who want to keep using Homie move to a normal subscription.',
          'Organisations without a budget line usually take the second. That is a real option, not a fallback.',
        ],
        hubLink: {
          before: 'Both models, and the story behind Homie, are set out on the ',
          label: 'partnerships overview',
          after: '.',
        },
      },
    ],

    cta: {
      heading: 'Start a conversation',
      blurb:
        'Tell us about one area you would want to start with, and we will send a one-page overview.',
      action: 'Contact us',
    },

    crossLink: 'Looking for the version for the people you employ?',
  },
}

export default en
